import React, { useState, useEffect, useRef, useMemo } from "react";
import { useUser } from "../../shared/context/UserContext";
import { getSites } from "../inventory/api/inventoryApi";
import { exportData, listProfiles, saveProfile, deleteProfile } from "./dataExchangeApi";
import { useDataExchangeManifest } from "./useDataExchangeManifest";

export default function ExportPanel() {
  const { callerEmail } = useUser();
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [selectedFields, setSelectedFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [showLoad, setShowLoad] = useState(false);
  const loadRef = useRef(null);

  const { fieldGroups, loading: manifestLoading, error: manifestError } = useDataExchangeManifest({ includeExportOnly: true });

  // Relabel "Key Fields" → "Tool (always included)" and mark it locked
  const exportFieldGroups = useMemo(() => fieldGroups.map(g => (
    g.label === "Key Fields"
      ? { ...g, label: "Tool (always included)", alwaysIncluded: true }
      : g
  )), [fieldGroups]);

  useEffect(() => {
    if (callerEmail) {
      listProfiles("export", callerEmail).then(setProfiles).catch(() => {});
    }
  }, [callerEmail]);

  useEffect(() => {
    function handleClick(e) {
      if (loadRef.current && !loadRef.current.contains(e.target)) setShowLoad(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  async function handleSaveTemplate() {
    if (!saveName.trim()) return;
    await saveProfile("export", saveName.trim(), selectedFields, callerEmail);
    setShowSave(false);
    setSaveName("");
    listProfiles("export", callerEmail).then(setProfiles).catch(() => {});
  }

  function handleLoadTemplate(profile) {
    setSelectedFields(profile.profile);
    setShowLoad(false);
  }

  async function handleDeleteTemplate(e, profileId) {
    e.stopPropagation();
    await deleteProfile(profileId, callerEmail);
    setProfiles(prev => prev.filter(p => p.id !== profileId));
  }

  useEffect(() => {
    getSites(callerEmail).then(data => {
      const list = Array.isArray(data) ? data : data.sites || [];
      setSites(list);
      if (list.length === 1) setSelectedSiteId(list[0].site_id);
    }).catch(() => {});
  }, [callerEmail]);

  function toggleField(key) {
    setSelectedFields(prev =>
      prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key]
    );
  }

  function toggleGroup(group) {
    // Locked fields are excluded: they are always exported, are never in
    // selectedFields, and would otherwise make allSelected permanently false.
    const groupKeys = group.fields.filter(f => !f.always_included).map(f => f.key);
    const allSelected = groupKeys.every(k => selectedFields.includes(k));
    if (allSelected) {
      setSelectedFields(prev => prev.filter(f => !groupKeys.includes(f)));
    } else {
      setSelectedFields(prev => [...new Set([...prev, ...groupKeys])]);
    }
  }

  async function handleDownload() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      // Mirrors export_data.py:29 KEY_FIELDS. The backend unions these in
      // anyway, so this prepend is redundant -- but a divergent copy is worse
      // than a redundant one, so it tracks the backend list exactly.
      const allFields = [...new Set(["supplier_tool_id", "tool_type", "die_op", "tool_id", ...selectedFields])];
      const data = await exportData({
        siteId: selectedSiteId,
        fields: allFields,
        callerEmail,
      });
      // Download directly from blob — bypasses SWA proxy entirely
      const a = document.createElement("a");
      a.href = data.download_url;
      a.download = data.filename;
      a.click();
      setResult({ filename: data.filename, row_count: data.row_count, tool_count: data.tool_count });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function siteLabel(site) {
    if (site.site_city) return `${site.site_name} (${site.site_city})`;
    if (site.supplier_site_code) return `${site.site_name} (${site.supplier_site_code})`;
    return site.site_name || "Unnamed Site";
  }

  const canDownload = selectedSiteId && selectedFields.length > 0 && !loading;

  return (
    <div className="de-panel">
      {/* Site picker */}
      <label className="de-label">Site</label>
      <select
        className="de-select"
        value={selectedSiteId}
        onChange={e => setSelectedSiteId(e.target.value)}
      >
        <option value="">Select a site...</option>
        {sites.map(s => (
          <option key={s.site_id} value={s.site_id}>{siteLabel(s)}</option>
        ))}
      </select>

      {/* Template bar */}
      <div className="de-profile-bar" style={{ marginTop: "var(--space-4)" }}>
        <div className="de-profile-actions">
          <div className="de-profile-load-wrap" ref={loadRef}>
            <button className="de-profile-btn" onClick={() => setShowLoad(!showLoad)}>
              Load Template &#9662;
            </button>
            {showLoad && (
              <div className="de-profile-dropdown">
                {profiles.length === 0 ? (
                  <div className="de-profile-dropdown-empty">No saved templates</div>
                ) : (
                  profiles.map(p => (
                    <div key={p.id} className="de-profile-dropdown-item" onClick={() => handleLoadTemplate(p)}>
                      <span className="de-profile-dropdown-name">{p.name}</span>
                      <button className="de-profile-dropdown-delete" onClick={e => handleDeleteTemplate(e, p.id)}>
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {showSave ? (
            <div className="de-profile-save-inline">
              <input
                type="text"
                className="de-profile-save-input"
                placeholder="Template name..."
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSaveTemplate()}
                autoFocus
              />
              <button className="de-profile-btn" onClick={handleSaveTemplate} disabled={!saveName.trim()}>Save</button>
              <button className="de-profile-btn" onClick={() => { setShowSave(false); setSaveName(""); }}>Cancel</button>
            </div>
          ) : (
            <button className="de-profile-btn" onClick={() => setShowSave(true)}>Save Template</button>
          )}
        </div>
      </div>

      {/* Field groups */}
      <label className="de-label" style={{ marginTop: "var(--space-3)" }}>Fields</label>
      {manifestLoading && <div>Loading field options...</div>}
      {manifestError && (
        <div className="de-banner de-banner-error">
          Could not load field manifest: {manifestError}
        </div>
      )}
      <div className="de-field-groups">
        {exportFieldGroups.map(group => {
          const isAlways = group.alwaysIncluded;
          const groupKeys = group.fields.filter(f => !f.always_included).map(f => f.key);
          const allSelected = isAlways || groupKeys.every(k => selectedFields.includes(k));

          return (
            <div key={group.label} className={`de-field-group ${isAlways ? "de-field-group-locked" : ""}`}>
              <div className="de-group-header">
                <span className="de-group-label">{group.label}</span>
                {!isAlways && (
                  <button
                    className="de-select-all"
                    onClick={() => toggleGroup(group)}
                  >
                    {allSelected ? "Deselect all" : "Select all"}
                  </button>
                )}
              </div>
              <div className="de-field-list">
                {group.fields.map(f => {
                  // Two independent locks, deliberately OR-ed rather than
                  // merged: isAlways is the existing GROUP lock (Key Fields),
                  // f.always_included is the new per-FIELD lock that reaches
                  // tool_id where the group lock cannot. The group match is
                  // left exactly as it was -- see P-11.
                  const locked = isAlways || !!f.always_included;
                  return (
                    <label key={f.key} className={`de-field-check ${locked ? "de-field-check-locked" : ""}`}>
                      <input
                        type="checkbox"
                        checked={locked || selectedFields.includes(f.key)}
                        disabled={locked}
                        onChange={() => !locked && toggleField(f.key)}
                      />
                      {f.label}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="de-export-footer">
        <span className="de-field-count">{selectedFields.length} field{selectedFields.length !== 1 ? "s" : ""} selected</span>
        <button
          className="btn btn-primary"
          disabled={!canDownload}
          onClick={handleDownload}
        >
          {loading ? "Exporting..." : "Download Export"}
        </button>
      </div>

      {/* Result / error banners */}
      {result && (
        <div className="de-banner de-banner-success">
          Downloaded {result.filename} — {result.tool_count} tools, {result.row_count} rows
        </div>
      )}
      {error && (
        <div className="de-banner de-banner-error">
          {error}
        </div>
      )}
    </div>
  );
}
